import React, { useMemo } from 'react';
import { Form, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import rolesConstants from '@utils/rolesConstants';
import { LorawanActionTypes } from '@utils/deviceActions';
import { DeviceLoraWanProps } from '@pages/LoraWan/types';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import { FormInstance } from 'antd/es/form/Form';

interface SuspensionReactivationPayloadProps {
  action: LorawanActionTypes.SUSPENSION | LorawanActionTypes.REACTIVATION;
  devices: {
    devEui: string;
  }[];
}

interface SuspensionReactivationProps {
  devices: DeviceLoraWanProps[];
  isUpload?: boolean;
  form: FormInstance<any>;
}

const useSuspensionReactivation = ({
  devices,
  isUpload,
  form,
}: SuspensionReactivationProps): DeviceActionProps => {
  const { t } = useTranslation(['lorawan', 'device-actions']);
  const actionType = Form.useWatch('actionType', form);

  const actionTypeOptions = useMemo(
    () => [
      {
        label: t('suspend'),
        value: LorawanActionTypes.SUSPENSION,
      },
      {
        label: t('reactivate'),
        value: LorawanActionTypes.REACTIVATION,
      },
    ],
    [t],
  );

  const steps: DeviceActionStepsProps[] = useMemo(
    () => [
      {
        title: t('whatDoYouWantToDo'),
        name: 'actionType',
        showWhenUpload: true,
        content: (
          <Radio.Group optionType="button" options={actionTypeOptions} />
        ),
      },
    ],
    [actionTypeOptions, t],
  );

  const payloadToSend = useMemo(
    (): SuspensionReactivationPayloadProps => ({
      action: actionType,
      devices: isUpload
        ? devices
        : devices?.map(device => ({
            devEui: device.devEui,
          })),
    }),
    [devices, actionType, isUpload],
  );

  const title = useMemo(() => {
    const labelPrefix = actionType ? 'by-key.' : '';
    const action = actionType || rolesConstants.LORAWAN_SUSPENSION_REACTIVATION;
    return t(`device-actions:labels.${labelPrefix}lorawan.${action}`);
  }, [t, actionType]);

  return {
    steps,
    payloadToSend,
    title,
    uploadPosition: 1,
  };
};

export default useSuspensionReactivation;
